import { useStore } from '@data/other/withStore';
import { useNavigate } from './useNavigate';
import useAsyncAction from '@data/hooks/useAsyncAction';
import { AddonList } from '@data/models';
import ReservationItem from '@data/models/ReservationItem';
import { Routes as AppRoutes, getPropertyRoot } from '@view/routes';
import { useCallback, useMemo } from 'react';
import TimeUtils from '@business/utils/time';
import { useParams } from 'react-router';

export const usePageNavigation = () => {
  const navigate = useNavigate();
  const { property, components } = useStore();
  const { addons, cart } = property;
  const { propertyCode } = useParams();

  const [searchAddons] = useAsyncAction<
    AddonList | null,
    [
      options: {
        propertyId: number;
        checkin: string;
        checkout: string;
        items: ReservationItem[];
      }
    ]
  >(useCallback(addons.search, []));

  const pageOrder = useMemo(
    () => [
      AppRoutes.Property,
      AppRoutes.Accommodations,
      AppRoutes.Addons,
      AppRoutes.AddGuest,
      AppRoutes.Payment,
      AppRoutes.ReservationConfirmed,
    ],
    []
  );

  const isHomePage = useMemo(() => {
    const root = getPropertyRoot();
    return location.pathname === root;
  }, [location.pathname]);
  const isAccommodationsPage = useMemo(
    () => location.pathname.includes(AppRoutes.Accommodations),
    [location.pathname]
  );
  const isGuestPage = useMemo(
    () => location.pathname.includes(AppRoutes.AddGuest),
    [location.pathname]
  );
  const isPaymentPage = useMemo(
    () => location.pathname.includes(AppRoutes.Payment),
    [location.pathname]
  );

  const getPreviousPage = useCallback(
    (page) => {
      const index = pageOrder.indexOf(page);
      if (index > 0) {
        return pageOrder[index - 1];
      }
      return pageOrder[0];
    },
    [pageOrder]
  );

  const goTo = async (path: string, from?: string) => {
    const next = (url: string) => {
      const root = getPropertyRoot();
      if (url !== root) url = root + '/' + url;
      url = url.replace(':propertyCode', propertyCode || '');
      navigate(url);
    };

    const totalAddons = addons.model?.items?.length || 0;

    if (path === AppRoutes.Addons) {
      if (from !== AppRoutes.Accommodations) {
        if (totalAddons === 0) {
          return next(AppRoutes.Accommodations);
        }
      } else {
        const addonsSearchPayload = {
          propertyId: property.model?.id || 0,
          checkin: TimeUtils.format(components.promoCode.value.checkin),
          checkout: TimeUtils.format(components.promoCode.value.checkout),
          items: cart.items,
        };
        const addonList = await searchAddons(addonsSearchPayload);
        if (addonList && addonList.items.length === 0) {
          return next(AppRoutes.AddGuest);
        }
      }
    }

    next(path);
  };

  return {
    goTo,
    getPreviousPage,
    isHomePage,
    isAccommodationsPage,
    isGuestPage,
    isPaymentPage,
  };
};
